/**
 * useGarage — RN port of the web hook in components/VehicleSelector.jsx.
 *
 * Stores a list of saved vehicles + the index of the active one in
 * AsyncStorage under the same keys as the web app, so a future shared
 * backend can sync them without remapping.
 *
 * `mounted` mirrors the web hook's hydration flag — screens use it to avoid
 * flashing "Select a Vehicle" before storage has loaded.
 */
import { useCallback, useEffect, useState } from 'react';
import { storage } from './storage';

const GARAGE_KEY = 'garageiq_garage';
const ACTIVE_KEY = 'garageiq_active_vehicle';

export function useGarage() {
  const [garage, setGarageState] = useState([]);
  const [activeIdx, setActiveIdxState] = useState(0);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [saved, savedIdx] = await Promise.all([
        storage.getItem(GARAGE_KEY),
        storage.getItem(ACTIVE_KEY),
      ]);
      if (cancelled) return;
      if (Array.isArray(saved)) setGarageState(saved);
      if (typeof savedIdx === 'number') setActiveIdxState(savedIdx);
      setMounted(true);
    })();
    return () => { cancelled = true; };
  }, []);

  const addVehicle = useCallback((v) => {
    setGarageState(prev => {
      const exists = prev.findIndex(
        p => p.make === v.make && p.model === v.model && p.year === v.year && p.engine === v.engine && p.trim === v.trim
      );
      const next = exists >= 0 ? prev : [...prev, v];
      const newIdx = exists >= 0 ? exists : next.length - 1;
      storage.setItem(GARAGE_KEY, next);
      storage.setItem(ACTIVE_KEY, newIdx);
      setActiveIdxState(newIdx);
      return next;
    });
  }, []);

  const removeVehicle = useCallback((idx) => {
    setGarageState(prev => {
      const next = prev.filter((_, i) => i !== idx);
      const newIdx = Math.min(activeIdx, Math.max(0, next.length - 1));
      storage.setItem(GARAGE_KEY, next);
      storage.setItem(ACTIVE_KEY, newIdx);
      setActiveIdxState(newIdx);
      return next;
    });
  }, [activeIdx]);

  const setActive = useCallback((idx) => {
    setActiveIdxState(idx);
    storage.setItem(ACTIVE_KEY, idx);
  }, []);

  const activeVehicle = mounted && garage.length > 0 ? garage[activeIdx] : null;

  return { garage, activeVehicle, activeIdx, addVehicle, removeVehicle, setActive, mounted };
}
